import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import React, { ReactNode, useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { separateCamelCase } from "../../lib/utils";
import { all_routes } from "../router/all_routes";
import { observer } from "mobx-react-lite";
import { useStore } from "../../app/stores/store";
import { IMAGE_SERVER_PATH } from "../../environment";
import { Dialog } from "primereact/dialog";
import { Button, Form, InputNumber, Modal } from "antd";
import userStore from "../../app/stores/userStore";
import toast from "react-hot-toast";

type Props = {
  data: any[];
  rowsPerPageOptions: number[];
  pageSize?: number;
  status?: ReactNode;
  exceptColumns: string[];
  extraColumns?: {
    header: string;
    accessor: string;
    render: (value: any, rowData?: any) => React.ReactNode;
  }[];
  setSelectedId?: (id: string) => void;
  routeUrl: string;
  dialogFlags: any;
  getViewId?: (id: string) => void;
};

const headerMap: Record<string, string> = {
  displayName: "Name",
  username: "Username",
  email: "Email",
  roles: "Roles",
  isPartner: "Is Partner",
  partnerApproved: "Partner Approved",
};

const TablePartner = ({
  data,
  rowsPerPageOptions,
  pageSize,
  status,
  exceptColumns,
  extraColumns = [],
  setSelectedId,
  routeUrl,
  dialogFlags,
  getViewId,
}: Props) => {
  const [rejectComment, setRejectComment] = useState("");
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [displayRejectModal, setDisplayRejectModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPaymentType, setSelectedPaymentType] = useState<string>("");
  const [bankCharge, setBankCharge] = useState(0);
  const [authCode, setAuthCode] = useState("");
  const { bookingStore, userStore } = useStore();

  const [bankChargeManuallyEdited, setBankChargeManuallyEdited] =
    useState(false);
  const [depositAmount, setDepositAmount] = useState<string>("");

  useEffect(() => {
    if (!bankChargeManuallyEdited) {
      if (selectedPaymentType === "Direct") {
        setBankCharge(0.01); // 1%
      } else if (selectedPaymentType === "Online") {
        setBankCharge(0.03); // 3%
      } else {
        setBankCharge(0);
      }
    }
  }, [selectedPaymentType, bankChargeManuallyEdited]);

  useEffect(() => {
    if (showPaymentModal) {
      setBankChargeManuallyEdited(false);
    }
  }, [showPaymentModal]);

  const parsedDepositAmount = parseFloat(depositAmount || "0");
  const partnerOnlyBankCharge = parsedDepositAmount * bankCharge;
  const partnerTotalWithBankCharge = (
    parsedDepositAmount + partnerOnlyBankCharge
  ).toFixed(2);
  // const partnerOnlyBankCharge = parseFloat(depositAmount || "0") * partnerBankCharge;
  const [selectedPartner, setSelectedPartner] = useState<any | null>(null);

  // const partnerTotalWithBankCharge = (parseFloat(depositAmount || "0") + partnerOnlyBankCharge).toFixed(2);

  const {
    userStore: { approvePartner, rejectPartner, loadPendingPartners, isAdmin },
    clientStore,
    bookingStore: { addPartnerPayment },
  } = useStore();

  // Handle Approve
  const handleApprove = async (id: string) => {
    try {
      await approvePartner(id);
      alert("Partner approved successfully.");
      await loadPendingPartners();
    } catch (error) {
      console.error("Approval error:", error);
      alert("Failed to approve partner.");
    }
  };

  // Handle Reject - open modal
  const handleReject = (id: string) => {
    setRejectingId(id);
    setRejectComment("");
    setDisplayRejectModal(true);
  };

  const handleConfirmPayment = async () => {
    const { id } = selectedPartner;

    if (!userStore.user?.id || !depositAmount) {
      alert("Partner or deposit information missing.");
      return;
    }

    if (!authCode.trim()) {
      alert("Please enter authorization code.");
      return;
    }

    const paymentPayload = {
      paymentType: selectedPaymentType,
      partnerId: id,
      amount: parseFloat(depositAmount),
      bankCharge: partnerOnlyBankCharge,
      token: "abc123",
      orderIds: "partner-security-deposit",
      payment: {
        transactionAmount: parseFloat(partnerTotalWithBankCharge),
        transactionID: authCode,
        createDate: new Date().toISOString(),
        currency: "AED",
        status: "Completed",
        transactionStatus: "Success",
        orderName: `Partner Security Deposit`,
        merchantId: "Partner123",
      },
    };

    try {
      const res = await addPartnerPayment(paymentPayload); // You'll implement this in the store
      toast.success("Partner payment recorded.");
      await userStore.getUSer(userStore.user?.id); // Refresh user info
      setShowPaymentModal(false);
      setAuthCode("");
    } catch (err) {
      toast.error("Partner payment failed.");
      console.error(err);
    }
  };

  // Submit rejection
  const handleSubmitReject = async () => {
    if (!rejectingId || !rejectComment.trim()) {
      alert("Please enter a rejection reason.");
      return;
    }



    try {
      const result = await rejectPartner(rejectingId, rejectComment);

      if (result.status === "success") {
        alert(result.data);
        await loadPendingPartners();
      } else {
        alert(`Error: ${result.error || "Unknown error occurred"}`);
      }
    } catch (error) {
      console.error("Complete rejection flow error:", error);
      alert(`Failed to reject partner: `);
    } finally {
      setDisplayRejectModal(false);
    }
  };

  const renderCell = (key: string, rowData: any) => {
    const value = rowData[key];

    switch (key) {
      case "roles":
        return value?.length ? value.join(", ") : "No Roles";

      case "isPartner":
        return value ? "Yes" : "No";

      case "partnerApproved":
        if (value === true)
          return <span className="text-green-600 font-semibold">Approved</span>;
        if (value === false)
          return <span className="text-red-600 font-semibold">Rejected</span>;
        return <span className="text-yellow-600 font-semibold">Pending</span>;

      case "companyLogoPath":
      case "tradeLicensePath":
        return value ? (
          <a
            href={`${IMAGE_SERVER_PATH}/${value}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            View File
          </a>
        ) : (
          <span className="text-gray-400 italic">No file</span>
        );

      case "createDate":
        return value
          ? new Date(value).toLocaleDateString("en-GB")
          : "Not available";

      default:
        return value;
    }
  };

  const rejectModalFooter = (
    <div>
      <button
        className="p-button p-button-text"
        onClick={() => setDisplayRejectModal(false)}
      >
        Cancel
      </button>
      <button className="p-button p-button-danger" onClick={handleSubmitReject}>
        Submit Rejection
      </button>
    </div>
  );

  return (
    <>
      <DataTable
        value={data}
        className="table datatable p-datatable-sm"
        rows={pageSize ?? 10}
        rowsPerPageOptions={rowsPerPageOptions}
        tableStyle={{ fontSize: "0.875rem" }}
      >
        {data &&
          Object.keys(data[0] || {})
            .filter((key) => !exceptColumns.includes(key))
            .map((key) => (
              <Column
                key={key}
                field={key}
                header={headerMap[key] ?? separateCamelCase(key)}
                body={(rowData: any) => renderCell(key, rowData)}
                style={{ fontSize: "0.875rem", padding: "8px" }}
              />
            ))}

        {extraColumns.map((col, idx) => (
          <Column
            key={col.accessor + idx}
            header={col.header}
            body={(rowData: any) => col.render(rowData[col.accessor], rowData)}
          />
        ))}

        <Column
          header="Uploaded Files"
          body={(rowData: any) => {
            const files = [
              { label: "Logo", path: rowData.companyLogoPath },
              { label: "License", path: rowData.tradeLicensePath },
            ];

            const links = files
              .filter((file) => file.path)
              .map((file, idx) => (
                <a
                  key={idx}
                  href={`${IMAGE_SERVER_PATH}/${file.path.replace(/\\/g, "/")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 me-2"
                  title={file.label}
                >
                  <i className="feather icon-eye"></i>
                </a>
              ));

            return links.length > 0 ? (
              <div className="flex">{links}</div>
            ) : (
              <span className="text-gray-400 italic">No files</span>
            );
          }}
        />
        <Column
          header="Manual Payment"
          body={(rowData: any) => (
            <Button
              onClick={() => {
                // Set the selected partner's data if needed
                setSelectedPartner(rowData); // ✅ Set selected partner
                setShowPaymentModal(true);
                setDepositAmount(""); // or rowData.defaultDeposit if applicable
                setAuthCode("");
                setSelectedPaymentType("");
                // Optional: set which partner is selected
                // setSelectedPartner(rowData);
              }}
            >
              Pay Now
            </Button>
          )}
        />

        {userStore.isAdmin() && (
          <Column
            header="Actions"
            body={(rowData: any) => (
              <div className="flex gap-3">
                <i
                  className="feather icon-check-circle text-green-600 cursor-pointer"
                  title="Approve"
                  onClick={() => handleApprove(rowData.id)}
                />
                <button
                  type="button"
                  title="Reject"
                  className="feather icon-x-circle text-red-600 cursor-pointer hover:text-red-800 bg-transparent border-none"
                  onClick={() => handleReject(rowData.id)}
                />
              </div>
            )}
          />
        )}
      </DataTable>

      <Modal
        title="Confirm Payment"
        visible={showPaymentModal}
        onCancel={() => setShowPaymentModal(false)}
        footer={null}
      >
        <div className="space-y-4">
          {/* Total Amount Display */}
          <div>
            <label className="block mb-1 font-semibold">Total Amount</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={depositAmount}
              onChange={(e) => setDepositAmount(e.target.value)}
              className="p-2 border rounded text-lg font-bold text-[#cc3093] w-full"
              placeholder="Enter deposit amount in AED"
            />

            {selectedPaymentType === "Online" &&
              depositAmount &&
              parseFloat(depositAmount) < 10000 && (
                <div className="text-red-600 text-sm mt-1">
                  Minimum deposit amount for online payment is AED 10,000.
                </div>
              )}
          </div>
          {/* Payment Type Selection */}
          <div>
            <label className="block mb-2 font-semibold">
              Select Payment Type
            </label>
            <div className="flex gap-2 flex-wrap">
              <Button
                type={selectedPaymentType === "Cash" ? "primary" : "default"}
                onClick={() => setSelectedPaymentType("Cash")}
              >
                Cash
              </Button>

              <Button
                type={selectedPaymentType === "Bank" ? "primary" : "default"}
                onClick={() => setSelectedPaymentType("Bank")}
              >
                Bank Transfer
              </Button>

              <Button
                type={selectedPaymentType === "Direct" ? "primary" : "default"}
                onClick={() => {
                  setSelectedPaymentType("Direct");
                  setBankChargeManuallyEdited(false);
                }}
              >
                POS Machine
              </Button>

              <Button
                type={selectedPaymentType === "Online" ? "primary" : "default"}
                onClick={() => {
                  setSelectedPaymentType("Online");
                  setBankChargeManuallyEdited(false);
                }}
              >
                Online Payment
              </Button>
            </div>
          </div>

          {/* Conditionally show Bank Charge input for Online or POS */}
          {(selectedPaymentType === "Online" ||
            selectedPaymentType === "Direct") && (
            <div>
              <label className="block mb-1 font-semibold">
                Bank Charge Rate (e.g., 0.01 = 1%)
              </label>
              <input
                type="number"
                min="0"
                step="0.0001"
                className="w-full p-2 border rounded"
                value={bankCharge}
                onChange={(e) => {
                  setBankCharge(parseFloat(e.target.value) || 0);
                  setBankChargeManuallyEdited(true);
                }}
                placeholder="Enter charge rate like 0.01 for 1%"
              />
              {/* Show total including bank charge */}
              <div className="mt-2 flex justify-between font-semibold text-[#cc3093]">
                <div>
                  Total with Bank Charge: {partnerTotalWithBankCharge} AED
                </div>
                <div>Bank Charge: {partnerOnlyBankCharge} AED</div>
              </div>
            </div>
          )}

          {/* Authorization Code Input */}
          <div>
            <label className="block mb-1">Authorization Code</label>
            <input
              className="w-full p-2 border rounded"
              value={authCode}
              onChange={(e) => setAuthCode(e.target.value)}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-2">
            <Button onClick={() => setShowPaymentModal(false)}>Cancel</Button>
            <Button
              type="primary"
              onClick={handleConfirmPayment}
              disabled={!selectedPaymentType || !authCode}
            >
              Confirm
            </Button>
          </div>
        </div>
      </Modal>

      {/* Reject Modal */}
      <Dialog
        header="Reject Partner"
        visible={displayRejectModal}
        style={{ width: "400px", borderRadius: "8px" }}
        onHide={() => setDisplayRejectModal(false)}
        footer={rejectModalFooter}
        modal
        closable
      >
        <div className="p-fluid p-4">
          <textarea
            className="w-full p-3 border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-red-500"
            placeholder="Enter rejection reason..."
            rows={5}
            value={rejectComment}
            onChange={(e) => setRejectComment(e.target.value)}
          />
        </div>
      </Dialog>
    </>
  );
};

export default observer(TablePartner);
